import { z } from "zod"

import { EXPENSE_MAX_TOTAL_MINOR, expenseCategorySchema } from "./expenses.ts"
import type { Feature } from "./features.ts"
import { tripCategorySchema, tripPlaceRequestSchema } from "./trips.ts"

/**
 * Language assistant contract (T341, docs/specs/language-assistant-spec.md section 2).
 * Request schemas are strict; the response schema is lenient (AGENTS.md "Contract and schema").
 */
export const ASSISTANT_INTENTS = [
  "create_trip",
  "update_trip",
  "delete_trip",
  "create_expense",
  "update_expense",
  "delete_expense",
  // P1: the server offers no tool for it until the invoicing module ships.
  "create_invoice",
] as const
export const assistantIntentSchema = z.enum(ASSISTANT_INTENTS)
export type AssistantIntent = z.infer<typeof assistantIntentSchema>

/** Which feature each intent belongs to; the server only offers tools for held features. */
export const ASSISTANT_INTENT_FEATURE: Record<AssistantIntent, Feature> = {
  create_trip: "logbook",
  update_trip: "logbook",
  delete_trip: "logbook",
  create_expense: "receipts",
  update_expense: "receipts",
  delete_expense: "receipts",
  create_invoice: "invoicing",
}

export const ASSISTANT_LOCALES = ["en-AU", "zh-CN"] as const
export const assistantLocaleSchema = z.enum(ASSISTANT_LOCALES)
export type AssistantLocale = z.infer<typeof assistantLocaleSchema>

export const ASSISTANT_TARGET_ENTITIES = ["trip", "expense"] as const
export const assistantTargetEntitySchema = z.enum(ASSISTANT_TARGET_ENTITIES)
export type AssistantTargetEntity = z.infer<typeof assistantTargetEntitySchema>

export const ASSISTANT_TEXT_MAX_LENGTH = 1000
export const ASSISTANT_HISTORY_MAX_TURNS = 6

/** Normalised fields: amounts in integer minor units, distance in integer metres, dates YYYY-MM-DD, times HH:MM (user time zone). */
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)

export const tripSlotsSchema = z
  .object({
    vehicle_id: z.uuid(),
    date: z.string().date(),
    start_time: hhmm,
    end_time: hhmm,
    duration_min: z
      .number()
      .int()
      .min(1)
      .max(24 * 60),
    distance_m: z.number().int().nonnegative(),
    start_odometer_km: z.number().int().nonnegative(),
    end_odometer_km: z.number().int().nonnegative(),
    category: tripCategorySchema,
    purpose: z.string().trim().max(500),
    start_place: tripPlaceRequestSchema,
    end_place: tripPlaceRequestSchema,
  })
  .partial()
export type TripSlots = z.infer<typeof tripSlotsSchema>

export const expenseSlotsSchema = z
  .object({
    vendor_name: z.string().trim().min(1).max(120),
    issued_on: z.string().date(),
    total_minor: z.number().int().min(1).max(EXPENSE_MAX_TOTAL_MINOR),
    gst_minor: z.number().int().min(0),
    // Whether the user said anything about GST (inclusive, exclusive or an amount).
    gst_stated: z.boolean(),
    category: expenseCategorySchema,
    business_pct: z.number().int().min(0).max(100),
    note: z.string().trim().max(500),
  })
  .partial()
export type ExpenseSlots = z.infer<typeof expenseSlotsSchema>

/** Slots schema for an intent; unknown keys are rejected. `null` intent allows no slots. */
export function slotsSchemaFor(intent: AssistantIntent | null): z.ZodType {
  switch (intent) {
    case "create_trip":
    case "update_trip":
    case "delete_trip":
      return tripSlotsSchema.strict()
    case "create_expense":
    case "update_expense":
    case "delete_expense":
      return expenseSlotsSchema.strict()
    default:
      // null and create_invoice (no slots defined yet)
      return z.object({}).strict()
  }
}

/** Entity an intent's target must name; `null` = the intent takes no target. */
function targetEntityFor(intent: AssistantIntent | null): AssistantTargetEntity | null {
  switch (intent) {
    case "update_trip":
    case "delete_trip":
      return "trip"
    case "update_expense":
    case "delete_expense":
      return "expense"
    default:
      return null
  }
}

/** Describes "which record"; resolved on the phone against local data. Never contains a record id. */
export const assistantTargetQuerySchema = z
  .object({
    entity: assistantTargetEntitySchema,
    date_from: z.string().date().optional(),
    date_to: z.string().date().optional(),
    // Place, vendor or purpose keyword.
    text: z.string().max(80).optional(),
    amount_minor: z.number().int().min(1).max(EXPENSE_MAX_TOTAL_MINOR).optional(),
    distance_m: z.number().int().min(1).max(2_000_000).optional(),
    vehicle_id: z.uuid().optional(),
    trip_category: tripCategorySchema.optional(),
    expense_category: expenseCategorySchema.optional(),
  })
  .strict()
  .refine((q) => !q.date_from || !q.date_to || q.date_from <= q.date_to, {
    path: ["date_to"],
    message: "date_from must not be after date_to",
  })
export type AssistantTargetQuery = z.infer<typeof assistantTargetQuerySchema>

/**
 * Lenient response-side twin of the target query: same keys, no bounds, unknown keys kept.
 * Clients treat an unknown `entity` as `unsupported`.
 */
export const assistantTargetQueryResponseSchema = z.looseObject({
  entity: z.string(),
  date_from: z.string().optional(),
  date_to: z.string().optional(),
  text: z.string().optional(),
  amount_minor: z.number().optional(),
  distance_m: z.number().optional(),
  vehicle_id: z.string().optional(),
  trip_category: z.string().optional(),
  expense_category: z.string().optional(),
})
export type AssistantTargetQueryResponse = z.infer<typeof assistantTargetQueryResponseSchema>

const counterMap = z
  .record(z.string().max(32), z.number().int().min(0).max(5))
  .refine((r) => Object.keys(r).length <= 16)

export const assistantTurnRequestSchema = z
  .object({
    text: z.string().trim().min(1).max(ASSISTANT_TEXT_MAX_LENGTH),
    // App language; the reply language follows the user's own words.
    locale: assistantLocaleSchema,
    // Device IANA time zone; the server validates it with Intl.
    time_zone: z.string().min(1).max(64),
    state: z
      .object({
        intent: assistantIntentSchema.nullable(),
        // Checked against the intent in superRefine below.
        slots: z.record(z.string().max(32), z.unknown()).refine((r) => Object.keys(r).length <= 16),
        target: assistantTargetQuerySchema.nullable(),
        // How many times each field has been asked about.
        asked: counterMap,
        history: z
          .array(
            z
              .object({
                role: z.enum(["user", "assistant"]),
                text: z.string().max(ASSISTANT_TEXT_MAX_LENGTH),
              })
              .strict(),
          )
          .max(ASSISTANT_HISTORY_MAX_TURNS),
      })
      .strict()
      .superRefine((state, ctx) => {
        const parsed = slotsSchemaFor(state.intent).safeParse(state.slots)
        if (!parsed.success) {
          ctx.addIssue({ code: "custom", path: ["slots"], message: "slots do not match intent" })
        }
        // The target may still be null mid-conversation for update/delete; when set it must fit.
        const wanted = targetEntityFor(state.intent)
        if (state.target && state.target.entity !== wanted) {
          ctx.addIssue({
            code: "custom",
            path: ["target"],
            message: "target does not match intent",
          })
        }
      }),
    context: z
      .object({
        vehicles: z
          .array(
            z
              .object({
                id: z.uuid(),
                // For example "Toyota Hilux ABC123".
                label: z.string().max(80),
                // The vehicle currently selected in the app (default for manual trips).
                is_default: z.boolean(),
              })
              .strict(),
          )
          .max(20),
        odometer_enabled: z.boolean(),
        gst_registered: z.boolean(),
      })
      .strict(),
  })
  .strict()
export type AssistantTurnRequest = z.infer<typeof assistantTurnRequestSchema>

/** Known statuses; the wire value is a lenient string, unknown values mean `unsupported`. */
export const assistantTurnStatusSchema = z.enum(["ready", "needs_info", "unsupported"])
export type AssistantTurnStatus = z.infer<typeof assistantTurnStatusSchema>

/** Unknown wire statuses mean `unsupported`. */
export function toKnownStatus(status: string): AssistantTurnStatus {
  const parsed = assistantTurnStatusSchema.safeParse(status)
  return parsed.success ? parsed.data : "unsupported"
}

export const assistantTurnResponseSchema = z.object({
  status: z.string(),
  intent: z.string().nullable(),
  slots: z.record(z.string(), z.unknown()),
  // Fields filled by default rules; the card marks them "default".
  defaulted: z.array(z.string()).default([]),
  missing: z.array(z.string()).default([]),
  // Always present for update/delete intents.
  target: assistantTargetQueryResponseSchema.nullable().default(null),
  // Question, explanation or card title, in the user's language.
  reply: z.string(),
  usage: z.object({ used: z.number().int(), monthly_limit: z.number().int().nullable() }),
})
export type AssistantTurnResponse = z.infer<typeof assistantTurnResponseSchema>

/**
 * Web "Say it" invoice draft (T472, invoice spec R2.10). Web has no on-device executor, so the
 * server parses the text into a PROPOSAL and the page applies it to the invoice form; nothing is
 * created until the user saves. The request carries only the text: the server is stateless and
 * stores no original text. Request strict, response lenient (AGENTS.md "Contract and schema").
 */
export const invoiceDraftRequestSchema = z
  .object({
    text: z.string().trim().min(1).max(ASSISTANT_TEXT_MAX_LENGTH),
  })
  .strict()
export type InvoiceDraftRequest = z.infer<typeof invoiceDraftRequestSchema>

export const invoiceDraftResponseSchema = z.object({
  // "ready" | "needs_info" | "unsupported"; unknown values mean unsupported.
  status: z.string(),
  // Set only when exactly one customer of the organisation matches the spoken name.
  customer_id: z.string().nullable().default(null),
  // What the user said when no single customer matched (empty = nothing was said).
  customer_name: z.string().nullable().default(null),
  // The customers that matched ambiguously, for the user to pick from.
  customer_candidate_ids: z.array(z.string()).default([]),
  reference: z.string().nullable().default(null),
  lines: z
    .array(
      z.object({
        description: z.string(),
        quantity_milli: z.number(),
        unit_amount_minor: z.number(),
        tax_code: z.string(),
      }),
    )
    .default([]),
  due_days: z.number().nullable().default(null),
  // Only when the user said whether prices include GST.
  amounts_are: z.string().nullable().default(null),
  missing: z.array(z.string()).default([]),
  reply: z.string(),
  usage: z.object({ used: z.number().int(), monthly_limit: z.number().int().nullable() }),
})
export type InvoiceDraftResponse = z.infer<typeof invoiceDraftResponseSchema>
